import React from 'react';

import { Space, type TableColumnsType } from 'antd-v5';
import Link from 'redux-first-router-link';
import { Text } from 'scouting-ui/lib';
import MemberTypeTag from 'scouting-ui/lib/Tags/MemberTypeTag';

import { MemberType } from '@modules/roster-lite/constants';
import {
  type UnitRosterMember,
  isYouth,
} from '@modules/roster-lite/models/unitRosterMember';
import MultiValueLabel from '@shared/components/MultiValueLabel';
import PersonAvatar from '@shared/components/PersonAvatar/PersonAvatar';
import { intl } from '@shared/localization';

interface FilterOption {
  text: string;
  value: string;
}

const EMPTY_CELL = '—';

/**
 * @param canOpenYouthProfile whether the CURRENT USER may open a youth's
 * advancement profile. When false, youth names render as plain text like
 * adults do. See `canViewYouthAdvancementDetailsSel`
 *
 * A youth also needs an `sb_user_id` to be linkable: the profile route is keyed
 * on the Scoutbook user id, and the report omits it on some rows.
 */
export function getColumns(
  guid: string,
  positionFilters: FilterOption[],
  subUnitFilters: FilterOption[],
  canOpenYouthProfile: boolean,
  isMobile: boolean,
): TableColumnsType<UnitRosterMember> {
  return [
    {
      title: intl.formatMessage({ id: 'shared.name' }),
      key: 'name',
      width: isMobile ? 120 : 220,
      minWidth: isMobile ? 100 : 160,
      fixed: 'left',
      render: (_: unknown, record) => {
        const avatar = (
          <PersonAvatar
            size="md"
            isAdult={record.memberType === MemberType.Adult}
            pictureUrl={undefined}
          />
        );
        return (
          <Space size="small" align="center">
            {avatar}
            {canOpenYouthProfile && isYouth(record) && record.sb_user_id ? (
              <Link to={`/unit-roster/${guid}/${record.sb_user_id}`}>
                <Text font="body-body-3" strong color="color-primary-blueLink">
                  {record.name}
                </Text>
              </Link>
            ) : (
              record.name
            )}
          </Space>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
    },
    {
      title: intl.formatMessage({ id: 'shared.memberId' }),
      dataIndex: 'member_id',
      key: 'member_id',
      width: 120,
      minWidth: 100,
      sorter: (a, b) =>
        a.member_id.localeCompare(b.member_id, undefined, { numeric: true }),
    },
    {
      title: intl.formatMessage({ id: 'shared.type' }),
      dataIndex: 'memberType',
      key: 'memberType',
      width: '15ch',
      minWidth: 40,
      filters: [
        {
          text: intl.formatMessage({ id: 'shared.youth' }),
          value: MemberType.Youth,
        },
        {
          text: intl.formatMessage({ id: 'shared.adult' }),
          value: MemberType.Adult,
        },
      ],
      onFilter: (value, record) => record.memberType === value,
      sorter: (a, b) => a.memberType.localeCompare(b.memberType),
      render: (value: MemberType) => <MemberTypeTag memberType={value} />,
    },
    {
      title: intl.formatMessage({ id: 'shared.rank' }),
      dataIndex: 'last_rank_earned',
      key: 'last_rank_earned',
      width: 140,
      sorter: (a, b) =>
        (a.last_rank_earned ?? '').localeCompare(b.last_rank_earned ?? ''),
      render: (value?: string) => value || EMPTY_CELL,
    },
    {
      title: intl.formatMessage({ id: 'unitRoster.table.subUnit' }),
      dataIndex: 'sub_unit',
      key: 'sub_unit',
      width: 180,
      filters: subUnitFilters,
      filterSearch: true,
      onFilter: (value, record) => record.sub_unit === String(value),
      sorter: (a, b) => (a.sub_unit ?? '').localeCompare(b.sub_unit ?? ''),
      render: (value?: string) => value || EMPTY_CELL,
    },
    {
      title: intl.formatMessage({ id: 'shared.position' }),
      dataIndex: 'positions',
      key: 'positions',
      width: 280,
      filters: positionFilters,
      filterSearch: true,
      onFilter: (value, record) => record.positions.includes(String(value)),
      sorter: (a, b) =>
        (a.positions[0] ?? '').localeCompare(b.positions[0] ?? ''),
      render: (values: string[]) => (
        <MultiValueLabel values={values} empty={EMPTY_CELL} />
      ),
    },
  ];
}
